import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, Upload, X } from 'lucide-react';
import API_ENDPOINTS, { getImageUrl } from '../../config/api';

const GiftManagement = () => {
  const [gifts, setGifts] = useState([]);
  const [editingGift, setEditingGift] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({ name: '', description: '', image_url: '', quantity_needed: 1, price: '', sort_order: 0 });

  useEffect(() => { fetchGifts(); }, []);

  const fetchGifts = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.ADMIN_GIFTS);
      console.log('Gifts response:', response.data);
      if (response.data.success) setGifts(response.data.gifts);
    } catch (error) { console.error('Error fetching gifts:', error);
    } finally { setLoading(false); }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) { alert('Invalid file type'); return; }
    if (file.size > 5 * 1024 * 1024) { alert('File too large. Max 5MB'); return; }

    const uploadFormData = new FormData();
    uploadFormData.append('image', file);
    uploadFormData.append('type', 'gifts');

    setUploading(true);
    try {
      const response = await axios.post(API_ENDPOINTS.UPLOAD_IMAGE, uploadFormData, { headers: { 'Content-Type': 'multipart/form-data' } });
      console.log('Upload response:', response.data);
      if (response.data.success) {
        setFormData(prev => ({ ...prev, image_url: response.data.url }));
        alert('Image uploaded successfully!');
      } else { alert(response.data.message); }
    } catch (error) { 
      console.error('Upload error:', error);
      alert('Error uploading image');
    } finally { setUploading(false); }
  };

  const handleSave = async () => {
    if (!formData.name) {
      alert('Please enter a gift name');
      return;
    }
    try {
      if (editingGift) await axios.put(API_ENDPOINTS.ADMIN_GIFTS, { ...formData, id: editingGift });
      else await axios.post(API_ENDPOINTS.ADMIN_GIFTS, formData);
      fetchGifts();
      setShowForm(false);
      setEditingGift(null);
      setFormData({ name: '', description: '', image_url: '', quantity_needed: 1, price: '', sort_order: 0 });
      alert('Gift saved successfully!');
    } catch (error) { 
      console.error('Save error:', error);
      alert('Error saving gift'); 
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this gift?')) {
      try {
        await axios.delete(API_ENDPOINTS.ADMIN_GIFTS, { data: { id } });
        fetchGifts();
        alert('Gift deleted successfully!');
      } catch (error) { alert('Error deleting gift'); }
    }
  };

  if (loading) return <div className="loading">Loading gifts...</div>;

  return (
    <div className="gift-management">
      <div className="section-header">
        <h1>Gift Registry Management</h1>
        <button className="btn-primary" onClick={() => { setEditingGift(null); setFormData({ name: '', description: '', image_url: '', quantity_needed: 1, price: '', sort_order: 0 }); setShowForm(true); }}><Plus size={18} /> Add Gift</button>
      </div>

      {showForm && (
        <div className="modal"><div className="modal-content">
          <h2>{editingGift ? 'Edit Gift' : 'Add Gift'}</h2>
          <div className="form-group"><label>Gift Name *</label><input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} /></div>
          <div className="form-group"><label>Description</label><textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows="3" /></div>
          <div className="form-group">
            <label>Gift Image</label>
            <div className="image-upload-container">
              {formData.image_url && (<div className="image-preview-wrapper"><img src={getImageUrl(formData.image_url)} alt="Preview" className="image-preview" /><button className="remove-image-btn" onClick={() => setFormData({...formData, image_url: ''})}><X size={16} /></button></div>)}
              <div className="upload-area">
                <input type="file" id="gift-image" accept="image/jpeg,image/png,image/jpg,image/gif,image/webp" onChange={handleImageUpload} disabled={uploading} style={{ display: 'none' }} />
                <button className="upload-btn" onClick={() => document.getElementById('gift-image').click()} disabled={uploading}><Upload size={18} />{uploading ? 'Uploading...' : formData.image_url ? 'Change Image' : 'Upload Image'}</button>
                <small>Max 5MB. JPG, PNG, GIF, WEBP</small>
              </div>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Quantity Needed *</label><input type="number" min="1" value={formData.quantity_needed} onChange={(e) => setFormData({...formData, quantity_needed: parseInt(e.target.value)})} /></div>
            <div className="form-group"><label>Price in KES (optional)</label><input type="number" min="0" step="1" value={formData.price} onChange={(e) => setFormData({...formData, price: e.target.value})} /></div>
          </div>
          <div className="modal-actions"><button className="btn-primary" onClick={handleSave}>Save</button><button className="btn-secondary" onClick={() => setShowForm(false)}>Cancel</button></div>
        </div></div>
      )}

      <div className="gifts-grid">
        {gifts.length === 0 ? (
          <div className="empty-state">No gifts added yet. Click "Add Gift" to get started.</div>
        ) : (
          gifts.map(gift => (
            <div key={gift.id} className="gift-admin-card">
              {gift.image_url && <img src={getImageUrl(gift.image_url)} alt={gift.name} onError={(e) => { e.target.src = 'https://placehold.co/200x200/e91e63/white?text=No+Image'; }} />}
              <div className="gift-info">
                <h3>{gift.name}</h3>
                <p>{gift.description}</p>
                {gift.price && (
                  <div className="gift-price-display">
                    KES {Math.round(parseFloat(gift.price))}
                  </div>
                )}
                <div className="gift-stats">
                  <span className={gift.remaining > 0 ? 'available' : 'unavailable'}>
                    Available: {gift.remaining} of {gift.quantity_needed}
                  </span>
                  <span>Reservations: {gift.reservations_count || 0}</span>
                </div>
                <div className="action-buttons">
                  <button onClick={() => { setEditingGift(gift.id); setFormData({ name: gift.name, description: gift.description || '', image_url: gift.image_url || '', quantity_needed: gift.quantity_needed, price: gift.price || '', sort_order: gift.sort_order || 0 }); setShowForm(true); }}><Edit size={18} /> Edit</button>
                  <button onClick={() => handleDelete(gift.id)}><Trash2 size={18} /> Delete</button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default GiftManagement;